import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLocationSelector from '../Components/CompanyLocationSelector';
import Sidebar from '../Components/Sidebar';
import AreaMultiSelect from '../Components/AreaMultiSelect';
import axios from '../api/axiosInstance';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import trident from '../assets/odin-trident.png';
import '../dashboard/dashboard.css';
import './OdinWorkspaceTheme.css';
import { logout } from '../auth/cookieUtils';

const Valves = () => {
  const navigate = useNavigate();

  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [areas, setAreas] = useState([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [valves, setValves] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortBy, setSortBy] = useState('valveTagNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleLogout = () => {
    logout();
  };

  useEffect(() => {
    if (selectedLocationId) {
      fetchAreas();
      setSelectedAreaIds([]);
    } else {
      setAreas([]);
      setSelectedAreaIds([]);
      setValves([]);
    }
    // eslint-disable-next-line
  }, [selectedLocationId]);

  useEffect(() => {
    if (!selectedLocationId) return;
    fetchValves();
    // eslint-disable-next-line
  }, [selectedLocationId, selectedAreaIds]);

  const fetchAreas = async () => {
    try {
      const response = await axios.get(`/api/areas/location/${selectedLocationId}`);
      setAreas(Array.isArray(response.data) ? response.data : []);

    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  const fetchValves = async () => {
    try {
      setLoading(true);

      let url = `/api/valves/by-location/${selectedLocationId}`;

      if (selectedAreaIds.length > 0) {
        selectedAreaIds.forEach((id, index) => {
          url += index === 0 ? `?areaIds=${id}` : `&areaIds=${id}`;
        });
      }

      const response = await axios.get(url);
      setValves(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching valves:', error);
      setValves([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (valveId) => {
    navigate(`/valves/${valveId}/profile`);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const getSortableValue = (valve, field) => {
    const value = valve[field];

    if (
      field === 'nextTestDate' ||
      field === 'nextRepairReplacement' ||
      field === 'lastTestDate' ||
      field === 'lastRepairReplacement'
    ) {
      return value ? new Date(value).getTime() : -Infinity;
    }

    return value ?? '';
  };

  const sortedValves = useMemo(() => {
    const sorted = [...valves].sort((a, b) => {
      const aValue = getSortableValue(a, sortBy);
      const bValue = getSortableValue(b, sortBy);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return sortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' })
        : String(bValue).localeCompare(String(aValue), undefined, { numeric: true, sensitivity: 'base' });
    });

    return sorted;
  }, [valves, sortBy, sortDirection]);

  const handleExportAll = async () => {
    if (sortedValves.length === 0) {
      alert('There are no valves to export for the selected filters.');
      return;
    }
    try {
      const data = sortedValves.map((valve) => ({
        'Valve Tag Number': valve.valveTagNumber || '—',
        'Valve Location': valve.valveLocation || '—',
        'Equipment Name': valve.equipmentName || '—',
        'Equipment Number': valve.equipmentNumber || '—',
        'Area': valve.areaName || '—',
        'Type': valve.type || '—',
        'Contents': valve.contents || '—',
        'Set Pressure': valve.setPressure || '—',
        'Size': valve.size || '—',
        'Test Frequency': valve.testFrequency || '—',
        'Last Test Date': valve.lastTestDate || '—',
        'Next Test Date': valve.nextTestDate || '—',
        'Repair Frequency': valve.repairFrequency || '—',
        'Last Repair/Replacement': valve.lastRepairReplacement || '—',
        'Next Repair/Replacement': valve.nextRepairReplacement || '—',
        'Manufacturer': valve.manufacturer || '—',
        'Model Number': valve.modelNumber || '—',
        'Serial Number': valve.serialNumber || '—',
        'Valve Capacity': valve.valveCapacity || '—',
        'Required Capacity': valve.requiredCapacity || '—',
      }));

      const ws = XLSX.utils.json_to_sheet(data);

      if (data.length > 0) {
        const headers = Object.keys(data[0]);

        ws['!cols'] = headers.map((header) => {
          const maxCellLength = Math.max(
            header.length,
            ...data.map((row) => String(row[header] ?? '').length)
          );

          return { wch: Math.max(maxCellLength + 2, 12) };
        });

        const lastCol = headers.length - 1;
        const lastRow = data.length;

        const ref = XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: lastRow, c: lastCol },
        });

        ws['!autofilter'] = { ref };
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Valves');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

      saveAs(
        new Blob([excelBuffer], { type: 'application/octet-stream' }),
        'valves.xlsx'
      );
    } catch (error) {
      console.error('Export failed:', error);
      alert('Unable to export valves. Please try again.');
    }
  };

  const valveSummary = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let totalValves = valves.length;
    let dueForTestCount = 0;
    let dueForReplacementCount = 0;
    let missingTestDateCount = 0;
    let missingReplacementDateCount = 0;
    let fullyCompliantCount = 0;

    valves.forEach((valve) => {
      const parsedTestDate = valve.nextTestDate ? new Date(valve.nextTestDate) : null;
      const parsedReplacementDate = valve.nextRepairReplacement ? new Date(valve.nextRepairReplacement) : null;
      const nextTestDate = parsedTestDate && !Number.isNaN(parsedTestDate.getTime()) ? parsedTestDate : null;
      const nextReplacementDate = parsedReplacementDate && !Number.isNaN(parsedReplacementDate.getTime()) ? parsedReplacementDate : null;

      if (nextTestDate) nextTestDate.setHours(0, 0, 0, 0);
      if (nextReplacementDate) nextReplacementDate.setHours(0, 0, 0, 0);

      const missingTestDate = !nextTestDate;
      const missingReplacementDate = !nextReplacementDate;
      const dueForTest = nextTestDate && nextTestDate <= today;
      const dueForReplacement = nextReplacementDate && nextReplacementDate <= today;

      if (missingTestDate) missingTestDateCount++;
      if (missingReplacementDate) missingReplacementDateCount++;
      if (dueForTest) dueForTestCount++;
      if (dueForReplacement) dueForReplacementCount++;

      const fullyCompliant =
        !missingTestDate &&
        !missingReplacementDate &&
        !dueForTest &&
        !dueForReplacement;

      if (fullyCompliant) fullyCompliantCount++;
    });

    return {
      totalValves,
      dueForTestCount,
      dueForReplacementCount,
      missingTestDateCount,
      missingReplacementDateCount,
      fullyCompliantCount,
    };
  }, [valves]);

  return (
    <div className="dashboard-page odin-workspace-page valves-workspace-page">
      <div className="dashboard-topbar">
        <div className="topbar-left">
          <img src={trident} alt="ODIN Logo" className="navbar-logo" />
          <CompanyLocationSelector
            onLocationChange={setSelectedLocationId}
          />
        </div>

        <div className="topbar-center">
          <span className="page-title">Valves</span>
        </div>

        <div className="topbar-right">
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-main">
        <Sidebar />

        <main className="dashboard-content workspace-content">
          <header className="workspace-page-heading">
            <span className="workspace-page-eyebrow">Pressure relief inventory</span>
            <h1>Valves</h1>
            <p>Review inspection readiness, compliance dates, and valve records for the selected location.</p>
          </header>

          {areas.length > 0 && (
            <section className="workspace-panel workspace-filter-panel">
              <div className="workspace-section-heading">
                <div>
                  <h2>Area filter</h2>
                  <p>{selectedAreaIds.length > 0 ? `${selectedAreaIds.length} selected` : 'Showing all areas'}</p>
                </div>
              </div>
              <AreaMultiSelect
                areas={areas}
                selectedAreaIds={selectedAreaIds}
                onChange={setSelectedAreaIds}
              />
            </section>
          )}

          {selectedLocationId && (
            <div className="valve-summary-row">
              <div className="summary-card">
                <h4>Total Valves</h4>
                <p>{valveSummary.totalValves}</p>
              </div>

              <div className="summary-card">
                <h4>Due for Test</h4>
                <p>{valveSummary.dueForTestCount}</p>
              </div>

              <div className="summary-card">
                <h4>Due for Replacement</h4>
                <p>{valveSummary.dueForReplacementCount}</p>
              </div>

              <div className="summary-card">
                <h4>Missing Test Date</h4>
                <p>{valveSummary.missingTestDateCount}</p>
              </div>

              <div className="summary-card">
                <h4>Missing Replacement Date</h4>
                <p>{valveSummary.missingReplacementDateCount}</p>
              </div>

              <div className="summary-card">
                <h4>Fully Compliant</h4>
                <p>{valveSummary.fullyCompliantCount}</p>
              </div>
            </div>
          )}

          <div className="workspace-results-toolbar">
            <div className="workspace-result-count">
              <span>Current results</span>
              <strong>{sortedValves.length.toLocaleString()} {sortedValves.length === 1 ? 'valve' : 'valves'}</strong>
            </div>
            <div className="export-buttons-row">
              <button type="button" className="export-btn" disabled={!selectedLocationId || loading || sortedValves.length === 0} onClick={handleExportAll}>
                Export valves
              </button>
            </div>
          </div>

          {!selectedLocationId ? (
            <div className="workspace-empty-state">Please select a location from the top bar to view valves.</div>
          ) : loading ? (
            <div className="workspace-empty-state">Loading valves...</div>
          ) : (
            <section className="workspace-table-panel">
              <div className="table-scroll-container">
                <table className="equipment-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('valveTagNumber')}>
                      Valve Tag Number {sortBy === 'valveTagNumber' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('valveLocation')}>
                      Valve Location {sortBy === 'valveLocation' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('equipmentName')}>
                      Parent Equipment {sortBy === 'equipmentName' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('equipmentNumber')}>
                      Equipment Number {sortBy === 'equipmentNumber' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('areaName')}>
                      Area {sortBy === 'areaName' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('type')}>
                      Type {sortBy === 'type' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('contents')}>
                      Contents {sortBy === 'contents' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('setPressure')}>
                      Set Pressure {sortBy === 'setPressure' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('size')}>
                      Size {sortBy === 'size' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('nextTestDate')}>
                      Next Test Date {sortBy === 'nextTestDate' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('nextRepairReplacement')}>
                      Next Repair/Replacement {sortBy === 'nextRepairReplacement' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('manufacturer')}>
                      Manufacturer {sortBy === 'manufacturer' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('modelNumber')}>
                      Model Number {sortBy === 'modelNumber' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('serialNumber')}>
                      Serial Number {sortBy === 'serialNumber' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('valveCapacity')}>
                      Valve Capacity {sortBy === 'valveCapacity' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('requiredCapacity')}>
                      Required Capacity {sortBy === 'requiredCapacity' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortedValves.length === 0 ? (
                    <tr>
                      <td className="workspace-table-empty" colSpan="16">
                        No valves found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    sortedValves.map((valve) => (
                      <tr
                        key={valve.id}
                        className="clickable-row"
                        onClick={() => handleRowClick(valve.id)}
                      >
                        <td>{valve.valveTagNumber || '—'}</td>
                        <td>{valve.valveLocation || '—'}</td>
                        <td>{valve.equipmentName || '—'}</td>
                        <td>{valve.equipmentNumber || '—'}</td>
                        <td>{valve.areaName || '—'}</td>
                        <td>{valve.type || '—'}</td>
                        <td>{valve.contents || '—'}</td>
                        <td>{valve.setPressure || '—'}</td>
                        <td>{valve.size || '—'}</td>
                        <td>{valve.nextTestDate || '—'}</td>
                        <td>{valve.nextRepairReplacement || '—'}</td>
                        <td>{valve.manufacturer || '—'}</td>
                        <td>{valve.modelNumber || '—'}</td>
                        <td>{valve.serialNumber || '—'}</td>
                        <td>{valve.valveCapacity || '—'}</td>
                        <td>{valve.requiredCapacity || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                </table>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default Valves;
